import * as swcHelpers from '@swc/helpers';
import { useState, useEffect, useCallback, useMemo } from 'react';
import { Selectors, State } from '@grandvisionhq/auth';
import { useLazyQuery, useMutation } from '@grandvisionhq/graphql';
import { useFeatureToggles, useSelector } from '@grandvisionhq/state';
import { UIType } from '../__generated__/sharedTypes.js';
import { CHECK_CUSTOMER_CONSENTS } from '../operations/queries/check-customer-consents.js';
import { SAVE_NEW_CONSENT } from '../operations/mutations/save-new-consent.js';
import { CREATE_EYE_EXAM } from '../operations/mutations/create-eye-exam.js';
import { createEyeExamError } from '../types/error.js';

/*
 * Check the eye-exam consent, create a new eye exam and returns the url. If the consent is not yet given, return the value of the consent and a function to save the consent.
 */ const useCreateEyeExam = ()=>{
    var ref3, ref1, ref2;
    const { featureConfigs  } = useFeatureToggles();
    // Check auth state
    const auth = useSelector(Selectors.getAuth);
    const isLoggedIn = State.isLoggedIn(auth);
    const isAuthLoading = State.getLoggedInState(auth) === 'loading';
    // @TODO add the correct error code mapped to BR label after https://jira.grandvision.global/browse/MACC-9133 is finished
    const [error, setError] = useState(null);
    const [checkConsent, checkConsentResult] = useLazyQuery(CHECK_CUSTOMER_CONSENTS);
    const [saveConsentMutation, saveResult] = useMutation(SAVE_NEW_CONSENT);
    const [createEyeExamUrl, createEyeExamMutation] = useMutation(CREATE_EYE_EXAM);
    // Consent name default is eye-exam, but can be changed by the opco
    const consentName = featureConfigs.eyeExamConsentKey;
    const loading = isAuthLoading || !checkConsentResult.called || checkConsentResult.loading || saveResult.loading || createEyeExamMutation.loading;
    const hasConsentOnLoad = checkConsentResult === null || checkConsentResult === void 0 ? void 0 : (ref3 = checkConsentResult.data) === null || ref3 === void 0 ? void 0 : ref3.checkCustomerConsents;
    const eyeExamUrl = (ref2 = (ref1 = createEyeExamMutation.data) === null || ref1 === void 0 ? void 0 : ref1.createEyeExam.eyeExam) === null || ref2 === void 0 ? void 0 : ref2.examUrl;
    // Check the eye-exam consent value if the user is logged in
    useEffect(()=>{
        if (isLoggedIn && !hasConsentOnLoad) {
            checkConsent({
                variables: {
                    consentName,
                    customerId: auth.userId
                }
            });
        }
    }, [
        isLoggedIn,
        checkConsent,
        hasConsentOnLoad,
        auth === null || auth === void 0 ? void 0 : auth.userId,
        consentName
    ]);
    const saveConsent = useCallback((consentText)=>{
        return saveConsentMutation({
            variables: {
                customerId: auth.userId,
                consents: [
                    {
                        consentName,
                        uiType: UIType.CHECKBOX,
                        value: true,
                        literalText: consentText
                    }, 
                ]
            }
        });
    }, [
        auth.userId,
        consentName,
        saveConsentMutation
    ]);
    const createEyeExam = useCallback(function() {
        var _ref = swcHelpers.asyncToGenerator(function*(consentText) {
            setError(null);
            if (!hasConsentOnLoad) {
                try {
                    var ref;
                    const result = yield saveConsent(consentText);
                    if (!(result === null || result === void 0 ? void 0 : (ref = result.data) === null || ref === void 0 ? void 0 : ref.saveNewConsents)) return;
                } catch (err) {
                    setError(createEyeExamError('SAVE_CONSENT', err));
                    return;
                }
            }
            try {
                return yield createEyeExamUrl({
                    variables: {
                        eyeExamInput: {
                            userId: auth.userId,
                            language: 'auto'
                        }
                    }
                });
            } catch (err) {
                setError(createEyeExamError('CREATE_URL', err));
            }
        });
        return function(consentText) {
            return _ref.apply(this, arguments);
        };
    }(), [
        saveConsent,
        createEyeExamUrl,
        auth.userId,
        hasConsentOnLoad
    ]);
    useEffect(()=>{
        // since checkConsent won't throw an error, we have to check it here
        if (checkConsentResult.error) {
            setError(createEyeExamError('CHECK_CONSENT', checkConsentResult.error));
        }
    }, [
        checkConsentResult.error
    ]);
    return useMemo(()=>{
        return {
            createEyeExam,
            eyeExamUrl,
            hasConsentOnLoad,
            loading,
            error
        };
    }, [
        hasConsentOnLoad,
        loading,
        error,
        createEyeExam,
        eyeExamUrl
    ]);
};

export { useCreateEyeExam };
//# sourceMappingURL=use-create-eye-exam.js.map
